import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  TrendingUp, TrendingDown, Award, Vote, Users as UsersIcon, CheckCircle,
  XCircle, MinusCircle, Calendar, Zap, Shuffle, Network, GraduationCap, Wallet,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, LineChart,
  Line, XAxis, YAxis, ReferenceLine,
} from "recharts";
import { NominateCard } from "@/components/NominateCard";
import { useLegislatorHistory, Sparkline, Callout, AnomalyFlag } from "@/components/profile/history";
import { useAuth } from "@/context/AuthContext";
import { LockedPanel, UpgradeNudge } from "@/components/entitlements/LockedPanel";
import { MoneyPanel } from "@/components/profile/MoneyPanel";
import { DistrictSnapshot } from "@/components/profile/DistrictSnapshot";
import { ordinal, statusPillClass } from "./shared";

const leadershipReadCache = new Map();
const LeadershipRead = ({ legislatorId, fallback }) => {
  const [read, setRead] = useState(leadershipReadCache.get(legislatorId) || null);
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    if (leadershipReadCache.has(legislatorId)) { setRead(leadershipReadCache.get(legislatorId)); return undefined; }
    const poll = async (attempt) => {
      try {
        const r = await axios.get(`${API}/legislators/${legislatorId}/leadership-read`, { validateStatus: (s) => s === 200 || s === 202 });
        if (cancelled) return;
        if (r.status === 202) { if (attempt < 12) timer = setTimeout(() => poll(attempt + 1), 5000); return; }
        leadershipReadCache.set(legislatorId, r.data);
        setRead(r.data);
      } catch { /* keep fallback */ }
    };
    poll(0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [legislatorId]);
  if (!read) return <p className="text-sm text-white/60 mt-2" data-testid="leadership-read-pending">{fallback} Writing a member-specific read of this role…</p>;
  return (
    <div className="mt-2" data-testid="leadership-read">
      <p className="text-sm text-white/75 leading-relaxed" data-testid="leadership-read-body">{read.body}{read.preview ? " …" : ""}</p>
      {read.preview && <UpgradeNudge feature="leadership_read" tier="pro" className="mt-3" text="Preview: Pro members see how this leader runs the office and who must go through it." />}
    </div>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (v) => {
  if (v == null) return null;
  const sign = v < 0 ? "−" : "";
  const a = Math.abs(v);
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  return `${sign}$${Math.round(a / 1e3).toLocaleString()}K`;
};

const NetWorthCard = ({ legislatorId }) => {
  const { featureMode } = useAuth();
  const locked = featureMode("wealth_analysis") === "none";
  const [nw, setNw] = useState(null);
  useEffect(() => {
    if (locked) return undefined;
    let alive = true;
    axios
      .get(`${API}/legislators/${legislatorId}/net-worth`, { timeout: 90000 })
      .then((r) => alive && setNw(r.data))
      .catch(() => alive && setNw({ known: false }));
    return () => { alive = false; };
  }, [legislatorId, locked]);

  if (locked) {
    return (
      <LockedPanel
        light
        feature="wealth_analysis"
        tier="pro"
        title="Estimated Net Worth"
        blurb="Live net-worth estimates from public financial disclosures, plus entry-vs-today wealth trajectory."
      />
    );
  }

  return (
    <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="networth-card">
      <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2" title="Midpoint of public financial disclosure ranges, with the trajectory since first taking office.">
        <Wallet className="w-5 h-5 text-orange-500" /> Estimated Net Worth
      </h2>
      {nw === null ? (
        <p className="text-sm text-gray-500 animate-pulse">Checking public disclosures…</p>
      ) : nw.known ? (
        <>
          <p className="text-3xl font-black font-mono tabular-nums text-gray-900" data-testid="networth-value">
            {fmtMoney(nw.midpointUSD)}
          </p>
          {nw.lowUSD != null && nw.highUSD != null && (
            <p className="text-xs text-gray-500 font-mono mt-1">
              Disclosure range: {fmtMoney(nw.lowUSD)}, {fmtMoney(nw.highUSD)}
            </p>
          )}
          {nw.note && <p className="text-sm text-gray-600 mt-2 leading-relaxed">{nw.note}</p>}
          {nw.entryUSD != null && nw.midpointUSD != null && (
            <div className="mt-3 pt-3 border-t border-gray-100" data-testid="networth-trajectory">
              <p className="text-[10px] font-mono uppercase tracking-wider text-gray-400 mb-1.5">
                Wealth Trajectory
              </p>
              <div className="flex items-baseline flex-wrap gap-x-2 gap-y-1">
                <span className="font-semibold text-gray-700 font-mono tabular-nums" data-testid="networth-entry-value">
                  {fmtMoney(nw.entryUSD)}
                </span>
                <span className="text-[10px] text-gray-400">
                  at entry{nw.entryYear ? ` (${nw.entryYear})` : ""}
                </span>
                <span className="text-gray-400">→</span>
                <span className="font-bold text-gray-900 font-mono tabular-nums">{fmtMoney(nw.midpointUSD)}</span>
                <span className="text-[10px] text-gray-400">today</span>
                {nw.entryUSD > 0 && (
                  <span
                    className={`text-xs font-mono font-bold px-1.5 py-0.5 rounded ${
                      nw.midpointUSD >= nw.entryUSD ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"
                    }`}
                    data-testid="networth-trajectory-pct"
                  >
                    {nw.midpointUSD >= nw.entryUSD ? "+" : ""}
                    {Math.round(((nw.midpointUSD - nw.entryUSD) / nw.entryUSD) * 100)}%
                  </span>
                )}
              </div>
              <p className="text-[10px] text-gray-400 mt-1.5">Entry figure via wealthincongress.com</p>
            </div>
          )}
          <p className="text-[11px] text-gray-400 mt-3">
            {nw.asOfYear ? `As of ${nw.asOfYear}` : "Latest available"}{nw.source ? ` · ${nw.source}` : ""}.
            Estimated from public financial disclosures, excludes primary residence and outstanding
            liabilities, so actual wealth can differ.
          </p>
        </>
      ) : (
        <p className="text-sm text-gray-500" data-testid="networth-unknown">
          No reliable public estimate available for this member.
        </p>
      )}
    </Card>
  );
};

/* eslint-disable no-unused-vars */
export const OverviewTab = ({
  legislator,
  legislatorId,
  loading,
  committees,
  lesScope,
  setLesScope,
  lesHistory
}) => {
  const history = useLegislatorHistory(legislatorId);
  return (
        <div className="grid gap-6 lg:grid-cols-3" data-testid="profile-tab-panel-overview">
          <div className="space-y-6">
            {/* Career Info */}
            <Card className="p-6 bg-white rounded-xl border-2 border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Career & Background</h2>
              <div className="space-y-3">
                {legislator.birthYear && (
                  <>
                    <div>
                      <p className="text-sm text-gray-600">Date of Birth</p>
                      <p className="font-semibold text-gray-900">
                        {legislator.birthYear} {legislator.birthYear && `(Age ${new Date().getFullYear() - parseInt(legislator.birthYear)})`}
                      </p>
                    </div>
                    <Separator />
                  </>
                )}
                
                {legislator.birthplace && (
                  <>
                    <div>
                      <p className="text-sm text-gray-600">Birthplace</p>
                      <p className="font-semibold text-gray-900">{legislator.birthplace}</p>
                    </div>
                    <Separator />
                  </>
                )}
                
                {(legislator.educationDetailed && legislator.educationDetailed.length > 0) ? (
                  <>
                    <div data-testid="education-detailed">
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <GraduationCap className="w-4 h-4" /> Education
                      </p>
                      <div className="mt-2 space-y-2">
                        {legislator.educationDetailed.map((edu, idx) => (
                          <div key={`${edu.institution}-${edu.degree}-${edu.year}`} className="border-l-2 border-blue-400 pl-3" data-testid={`education-item-${idx}`}>
                            <p className="font-semibold text-gray-900">
                              {edu.degree && <span className="text-blue-700 mr-1">{edu.degree}</span>}
                              {edu.institution}
                            </p>
                            {edu.year && <p className="text-xs text-gray-500">Class of {edu.year}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                    <Separator />
                  </>
                ) : legislator.education && legislator.education.length > 0 ? (
                  <>
                    <div>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <GraduationCap className="w-4 h-4" /> Education
                      </p>
                      {legislator.education.map((edu, idx) => (
                        <p key={edu} className="font-semibold text-gray-900">{edu}</p>
                      ))}
                    </div>
                    <Separator />
                  </>
                ) : null}
                
                {legislator.previousEmployment && legislator.previousEmployment.length > 0 && (
                  <>
                    <div data-testid="previous-employment">
                      <p className="text-sm text-gray-600 mb-1.5">Previous Employment</p>
                      <ul className="space-y-1.5">
                        {(legislator.previousEmploymentDetail?.length
                          ? legislator.previousEmploymentDetail
                          : legislator.previousEmployment.map((job) => ({ role: job, organization: "", years: "" }))
                        ).map((job, idx) => (
                          <li key={`${job.role}-${job.organization}-${idx}`} className="flex gap-2 text-sm leading-snug" data-testid="previous-employment-item">
                            <span className="text-orange-500 mt-[3px] shrink-0" aria-hidden="true">&#9679;</span>
                            <span className="min-w-0">
                              <span className="font-semibold text-gray-900">{job.role || job.organization}</span>
                              {job.role && job.organization && <span className="text-gray-700">, {job.organization}</span>}
                              {job.years && <span className="text-gray-500 font-mono text-xs ml-1.5 whitespace-nowrap">{/-$/.test(job.years) ? `from ${job.years.replace(/-$/, "")}` : job.years}</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Separator />
                  </>
                )}

                {legislator.netWorth && (legislator.netWorth.current || legislator.netWorth.atFirstElection) && null}
                
                <div>
                  <p className="text-sm text-gray-600">First Elected</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(legislator.elected).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                  </p>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-gray-600">Next Election</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(legislator.nextElection).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                  </p>
                </div>
                <Separator />
                <div>
                  <p className="text-sm text-gray-600">Years of Service</p>
                  <p className="font-semibold text-gray-900">{legislator.yearsInOffice} years</p>
                </div>
              </div>
            </Card>

            {/* Estimated Net Worth */}
            <NetWorthCard legislatorId={legislatorId} />
            <MoneyPanel legislatorId={legislatorId} />
            <DistrictSnapshot legislatorId={legislatorId} />

          </div>
          <div className="lg:col-span-2 space-y-6">
            {legislator.leadershipRole && (
            <Card className="p-6 rounded-xl border border-orange-500/40" style={{ background: "linear-gradient(135deg, rgba(255,106,0,0.14), rgba(11,11,15,0.9))" }} data-testid="leadership-role-card">
              <p className="text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-orange-300/90 mb-2">Chamber Leadership</p>
              <h2 className="text-2xl font-black text-white leading-tight">{legislator.leadershipRole}</h2>
              <LeadershipRead legislatorId={legislatorId} fallback={`${legislator.leadershipCaucus === "majority" ? "Majority" : "Minority"} leadership, ${legislator.chamber}.`} />
              {committees.length > 0 && (
                <p className="text-xs text-white/45 mt-3">Also serves on: {committees.slice(0, 3).map((c) => (typeof c === "string" ? c : c.name)).join(" · ")}</p>
              )}
            </Card>
            )}
            {/* Political Ideology. DW-NOMINATE */}
            <NominateCard legislatorId={legislatorId} history={history} />
            <Callout history={history} metric="ideology" />

            {/* Stats Summary */}
            <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="stats-summary-card">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Statistics Summary</h2>
              <p className="text-xs text-gray-500 mb-4">Key performance metrics with per-Congress trend where history exists</p>
              <div className="space-y-3">
                <div title="Share of roll-call votes where this member voted with their party's majority position, 119th Congress.">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-600">Party Alignment<AnomalyFlag history={history} metric="partyAlignment" /></span>
                    <span className="font-semibold text-gray-900">{legislator.stats.partyAlignmentPercent.toFixed(1)}%</span>
                  </div>
                  <p className="text-xs text-gray-500">Percentage of votes aligned with party majority position. 119th Congress only, earlier roll-call history is not in the WHIP cache.</p>
                  <Callout history={history} metric="partyAlignment" />
                </div>
                <Separator />
                <div title="Of opposite-party members voting on the same roll calls, the share who voted the same way as this member. Higher means more cross-party voting.">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-600">Bipartisan Index<AnomalyFlag history={history} metric="bipartisanIndex" /></span>
                    <span className="font-semibold text-gray-900">{legislator.stats.bipartisanIndex.toFixed(1)}</span>
                  </div>
                  <p className="text-xs text-gray-500">Out of every 100 cross-party peers voting on the same roll call, the share that voted the same way. 119th Congress only.</p>
                  <Callout history={history} metric="bipartisanIndex" />
                </div>
                <Separator />
                <div title="Votes cast out of roll calls held while seated. The sparkline shows participation rate per Congress from Voteview.">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-600">Total Votes<AnomalyFlag history={history} metric="totalVotes" /></span>
                    <span className="font-semibold text-gray-900">{legislator.stats.votesCast} / {legislator.stats.votesCount}</span>
                  </div>
                  <p className="text-xs text-gray-500">Votes cast out of total votes held during tenure. Trend: participation rate per Congress.</p>
                  <Sparkline history={history} field="participation" format={(v) => `${v}%`} testId="sparkline-participation" />
                  <Callout history={history} metric="totalVotes" />
                </div>
                <Separator />
                <div title="Percentage of sponsored bills that became law. The sparkline shows the enactment rate per Congress.">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-gray-600">Legislative Success Rate<AnomalyFlag history={history} metric="successRate" /></span>
                    <span className="font-semibold text-gray-900">
                      {legislator.stats.billsSponsored ? ((legislator.stats.billsPassed / legislator.stats.billsSponsored) * 100).toFixed(1) : "0.0"}%
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">Percentage of sponsored bills directly enacted into law (Congress.gov records)</p>
                  <Sparkline history={history} field="successRate" format={(v) => `${v}%`} color="#16a34a" testId="sparkline-success" />
                  <Callout history={history} metric="successRate" />
                  <Callout history={history} metric="billsSponsored" />
                </div>
              </div>
            </Card>


            {/* Legislative Effectiveness Score (LES). Volden. Wiseman */}
            {legislator.stats?.effectivenessScore > 0 && legislator.stats?.effectivenessBreakdown && (() => {
              const stats = legislator.stats;
              const hasCurrent =
                stats.effectivenessBreakdownCurrent &&
                stats.effectivenessScoreCurrent !== null &&
                stats.effectivenessScoreCurrent !== undefined;
              const scope = hasCurrent && lesScope === "current" ? "current" : "career";
              const activeScore = scope === "current" ? stats.effectivenessScoreCurrent : stats.effectivenessScore;
              const activeBreakdown = scope === "current" ? stats.effectivenessBreakdownCurrent : stats.effectivenessBreakdown;
              const trendData = lesHistory
                .map((h) => ({
                  date: h.date,
                  value: scope === "current" ? h.scoreCurrent : h.score,
                }))
                .filter((d) => d.value !== null && d.value !== undefined);
              return (
                <Card
                  className="p-6 bg-white rounded-xl border-2 border-orange-500/40"
                  data-testid="legislative-effectiveness-card"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-xl font-bold text-gray-900" title="Volden-Wiseman Legislative Effectiveness Score: how far this member's bills travel (action, committee, chamber passage, law) relative to the chamber average. 1.0 = average.">Legislative Effectiveness</h2>
                        {stats.lesTrend?.direction === "rising" && (
                          <span
                            data-testid="les-trend-badge"
                            title={`LES up ${stats.lesTrend.delta} between ${stats.lesTrend.from} and ${stats.lesTrend.to}`}
                            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                          >
                            <TrendingUp className="w-3 h-3" /> Rising Effectiveness
                          </span>
                        )}
                        {stats.lesTrend?.direction === "falling" && (
                          <span
                            data-testid="les-trend-badge"
                            title={`LES down ${stats.lesTrend.delta} between ${stats.lesTrend.from} and ${stats.lesTrend.to}`}
                            className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-400 border border-rose-500/30"
                          >
                            <TrendingDown className="w-3 h-3" /> Falling Effectiveness
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Volden. Wiseman LES · vs {legislator.stats.effectivenessChamber} peers ·{" "}
                        <span className="font-medium">1.00 = chamber average</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <div
                        className={`text-4xl font-black tabular-nums ${
                          activeScore >= 1.5
                            ? "text-emerald-400"
                            : activeScore >= 1.0
                            ? "text-orange-400"
                            : activeScore >= 0.5
                            ? "text-amber-400"
                            : "text-gray-500"
                        }`}
                        data-testid="les-score"
                      >
                        {Number(activeScore).toFixed(2)}
                      </div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-0.5">
                        {activeScore >= 1.0
                          ? `${Number(activeScore).toFixed(1)}× more effective than avg`
                          : "below chamber average"}
                      </div>
                    </div>
                  </div>

                  {/* Scope toggle, traditional per-Congress LES vs full career */}
                  <div className="flex gap-1.5 mb-4" data-testid="les-scope-toggle">
                    <button
                      onClick={() => setLesScope("current")}
                      disabled={!hasCurrent}
                      data-testid="les-scope-current"
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        scope === "current"
                          ? "bg-orange-500 text-white border-orange-500"
                          : "bg-transparent text-gray-500 border-gray-300 hover:border-orange-400"
                      } ${!hasCurrent ? "opacity-40 cursor-not-allowed" : ""}`}
                      title={!hasCurrent ? "Per-Congress data is still being computed, check back soon" : ""}
                    >
                      119th Congress
                    </button>
                    <button
                      onClick={() => setLesScope("career")}
                      data-testid="les-scope-career"
                      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        scope === "career"
                          ? "bg-orange-500 text-white border-orange-500"
                          : "bg-transparent text-gray-500 border-gray-300 hover:border-orange-400"
                      }`}
                    >
                      Career
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: "action", label: "Action Beyond Intro", desc: "got any action past introduction" },
                      { key: "committee", label: "Out of Committee", desc: "were reported out of committee" },
                      { key: "chamber", label: "Passed Chamber", desc: "passed a floor vote" },
                      { key: "law", label: "Became Law", desc: "were signed into law" },
                    ].map(({ key, label, desc }) => {
                      const row = activeBreakdown[key];
                      if (!row) return null;
                      const pct = Math.min(100, Math.max(4, (row.ratio || 0) * 50)); // 2× avg fills the bar
                      return (
                        <div
                          key={key}
                          className="p-3 bg-gray-100 rounded-lg border border-gray-200"
                          data-testid={`les-breakdown-${key}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-semibold text-gray-700">{label}</span>
                            <span
                              className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                row.ratio >= 1.0
                                  ? "bg-emerald-500/15 text-emerald-400"
                                  : "bg-gray-500/20 text-gray-400"
                              }`}
                            >
                              {Number(row.ratio).toFixed(1)}× avg
                            </span>
                          </div>
                          <div className="flex items-baseline gap-1.5 mb-1.5">
                            <span className="text-2xl font-black text-gray-900 tabular-nums">{row.member}</span>
                            <span className="text-[11px] text-gray-500">
                              bills · chamber avg {Number(row.avg).toFixed(1)}
                            </span>
                          </div>
                          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${row.ratio >= 1.0 ? "bg-orange-500" : "bg-gray-400"}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <p className="text-[10px] text-gray-500 mt-1">
                            {scope === "current" ? "This Congress" : "Career"}: {row.member} of their bills {desc}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  {/* LES trend over time */}
                  <div className="mt-4" data-testid="les-trend-section">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-bold text-gray-900" title="Daily snapshots of the effectiveness score. Rising means bills are advancing faster than peers' this session.">LES Trend</h3>
                      <span className="text-[10px] text-gray-500">recorded daily · dashed line = chamber avg</span>
                    </div>
                    {trendData.length >= 2 ? (
                      <div className="h-36" style={{ minHeight: 144 }} data-testid="les-trend-chart">
                        <ResponsiveContainer width="100%" height={144}>
                          <LineChart data={trendData} margin={{ top: 5, right: 8, bottom: 0, left: -20 }}>
                            <XAxis
                              dataKey="date"
                              tick={{ fontSize: 10, fill: "#9ca3af" }}
                              tickLine={false}
                              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                            />
                            <YAxis
                              tick={{ fontSize: 10, fill: "#9ca3af" }}
                              tickLine={false}
                              axisLine={false}
                              domain={["auto", "auto"]}
                            />
                            <ReferenceLine y={1} stroke="#9ca3af" strokeDasharray="4 4" />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#16161d",
                                border: "1px solid rgba(255,138,0,0.3)",
                                borderRadius: 8,
                                fontSize: 12,
                              }}
                              labelStyle={{ color: "#9ca3af" }}
                              formatter={(v) => [Number(v).toFixed(2), "LES"]}
                            />
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke="#FF8A00"
                              strokeWidth={2}
                              dot={{ r: 2.5, fill: "#FF8A00" }}
                              activeDot={{ r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 bg-gray-100 rounded-lg p-3" data-testid="les-trend-empty">
                        {trendData.length === 1
                          ? `Tracking started, first snapshot recorded ${trendData[0].date}. The trend line will build as daily scores accumulate.`
                          : "Score tracking has just begun for this member. Check back soon to see their effectiveness trend over time."}
                      </p>
                    )}
                  </div>

                  <details className="mt-4 text-xs text-gray-600">
                    <summary className="cursor-pointer font-medium text-orange-700 hover:text-orange-800">
                      How is this calculated?
                    </summary>
                    <div className="mt-2 space-y-1.5 pl-4 text-[11px] leading-relaxed">
                      <p>
                        <strong>LES = ¼·(BillS/Avg) + ¼·(BillC/Avg) + ¼·(BillP/Avg) + ¼·(BillL/Avg)</strong>
                      </p>
                      <p>
                        Each of the four terms compares the legislator's count at that stage to the
                        average for their chamber (House / Senate averages are computed separately).
                        A score of <span className="font-mono">1.00</span> means "average for
                        chamber". <span className="font-mono">2.00</span> means twice as effective;{" "}
                        <span className="font-mono">0.50</span> means half. Only substantive
                        legislation counts, bills (H.R./S.) and joint resolutions
                        (H.J.Res./S.J.Res.). Simple and concurrent resolutions are excluded because
                        they can't become law.
                      </p>
                      <p>
                        <strong>119th Congress</strong> follows the traditional Volden. Wiseman scope
                        (bills sponsored in the current Congress only). <strong>Career</strong> counts
                        every bill the member has ever sponsored, long-serving members naturally show
                        larger numbers there. Methodology adapted from the Center for Effective
                        Lawmaking (UVA / Vanderbilt).
                      </p>
                    </div>
                  </details>
                </Card>
              );
            })()}
          </div>
        </div>
  );
};
